import { SetStateAction, useEffect, useState, useRef } from "react";
import { useRouter } from "next/router";
import { getAuctionEventById, NonIndividualBidderTypeValue } from "services";
import { v4 as uuidv4 } from "uuid";
import CustomRejectionModal from "../CustomRejectionModal";
import CustomErrorHandlingModal from "../CustomErrorHandlingModal";
import BankerPageNotFound from "../../BankerPageNotFound";
import {
  bidderDataInitialise,
  useGetBasicDetails,
  getTeamMembersEmailDetails,
  handleAcceptBidder,
  handleBidderDataSubmit,
  handleRejectBidder,
  handlePANFieldValidations,
  focusFieldById,
  useInitializeBetaForms,
  kycDetailsSwitchFields,
  AddBidderFormType,
} from "../bidderFormServices/getDetails";
import {
  AcceptRejectComponent,
  BidderDocumentUploader,
  BidderFormFieldsHelper,
  BidderPDFViewer,
  NotificationModal,
  SubmitButtonComponent,
} from "../bidderFormServices/BidderFormFieldsHelper";
import { getCompanyFormFieldsConfig } from "./CompanyBidderFormConfig";
import { getPartnershipFormFieldsConfig } from "./PartnershipBidderFormConfig";
import { getProprietorsFormFieldsConfig } from "./ProprietorsBidderFormConfig";

const isDigioPanFullVerificationEnabled =
  process.env.NEXT_PUBLIC_DIGIO_PAN_FULL_VERIFICATION_ENABLED === "true";

export type NonIndividualBidderType = NonIndividualBidderTypeValue;

interface BidderFormProps {
  mode?: string;
  allBiddersData?: any;
  currentBidderData?: any;
  activeStep?: number;
  setActiveStep?: React.Dispatch<SetStateAction<number>>;
  bidderFormWrapperClassName?: string;
  setCurrentBidderData?: React.Dispatch<SetStateAction<Array<any>>>;
  user_type?: NonIndividualBidderType | AddBidderFormType;
}
export const NonIndividualBidderForm = ({
  mode,
  bidderFormWrapperClassName,
  user_type,
}: BidderFormProps) => {
  const router = useRouter();
  const { for_event } = router.query;
  const { id } = router.query;
  const [event, setEvent] = useState<any>([]);
  const [renderKey, setRenderKey] = useState(0);
  const [bidderData, setBidderData] = useState<any>();
  const [membersEmail, setMembersEmail] = useState<any>([]);
  const [kycFiles, setKycFiles] = useState<any>([]);
  const [jointDeclarationFiles, setJointDeclarationFiles] = useState<any>([]);
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [randomId, setRandomId] = useState<any>();
  const [isFetchingPan, setIsFetchingPan] = useState(false);
  const [disablePan, setDisablePan] = useState(false);
  const [isNameVerified, setIsNameVerified] = useState(false);
  type ErrorType = "validation" | "api";
  const [isErrorHandlingModalOpen, setIsErrorHandlingModalOpen] = useState<{
    open: boolean;
    type: ErrorType;
  }>({
    open: false,
    type: "validation",
  });
  const [errorHandlingModalData, setErrorHandlingModalData] =
    useState<any>(null);

  const [isLoading, setIsLoading] = useState(false);
  const [isOverwrite, setIsOverwrite] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [notificationPopup, setNotificationPopup] = useState("");

  const { banker, currentBranch, bank } = useGetBasicDetails();

  // V4 name-first flow: debounce ref for PAN verification trigger
  const panVerificationTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Debounced function to trigger PAN verification when name >= 3 chars
  const triggerPanVerification = () => {
    if (panVerificationTimeoutRef.current) {
      clearTimeout(panVerificationTimeoutRef.current);
    }
    panVerificationTimeoutRef.current = setTimeout(() => {
      const nameValue = form?.getField?.("name");
      const panValue = form?.getField?.("pan");

      // Only trigger if name >= 3 AND PAN has value
      if (
        nameValue &&
        nameValue.length >= 3 &&
        panValue &&
        panValue.length >= 10 &&
        isDigioPanFullVerificationEnabled
      ) {
        handlePANFieldValidations(
          { target: { value: panValue } },
          form,
          setIsFetchingPan,
          setIsNameVerified,
        );
      }
    }, 500);
  };

  useEffect(() => {
    if ((id && mode === "view") || mode === "edit") {
      (async () => {
        await bidderinit(id);
      })();
    }
    if (for_event) {
      (async () => {
        await init(for_event);
      })();
    }

    getTeamMembersEmailDetails(currentBranch, setMembersEmail);
  }, [for_event, id]);

  useEffect(() => {
    if (mode === "create") setRandomId(uuidv4());
  }, []);

  const init = async (id: any) => {
    const data: any = await getAuctionEventById(id);
    setEvent(data);
  };

  const bidderinit = async (id: any) => {
    const data = await bidderDataInitialise(
      id,
      setBidderData,
      setKycFiles,
      setJointDeclarationFiles,
      setRenderKey,
    );
    if (!data) {
      setNotFound(true);
    }
  };

  const form = useInitializeBetaForms(
    bidderData,
    membersEmail,
    mode ?? "view",
    setIsNameVerified,
    user_type as AddBidderFormType,
  );

  const getNonIndividualFormFieldsConfig = (user_type: string) => {
    if (
      user_type === "partnership" ||
      user_type === "partnershipfirm" ||
      user_type === "llp" ||
      user_type === "llpfirm"
    )
      return getPartnershipFormFieldsConfig(
        form,
        mode,
        setIsFetchingPan,
        setIsNameVerified,
        isNameVerified,
        disablePan,
        setDisablePan,
        setNotificationPopup,
        triggerPanVerification,
      );

    if (user_type === "proprietors")
      return getProprietorsFormFieldsConfig(
        form,
        mode,
        setIsFetchingPan,
        setIsNameVerified,
        isNameVerified,
        disablePan,
        setDisablePan,
        setNotificationPopup,
        triggerPanVerification,
      );

    if (
      user_type === "company" ||
      user_type === "trust" ||
      user_type === "huf" ||
      user_type === "aop" ||
      user_type === "boi" ||
      user_type === "govt" ||
      user_type === "local" ||
      user_type === "ajp"
    )
      return getCompanyFormFieldsConfig(
        form,
        mode,
        setIsFetchingPan,
        setIsNameVerified,
        isNameVerified,
        disablePan,
        setDisablePan,
        setNotificationPopup,
        triggerPanVerification,
      );
  };

  // Keep `initial_bid_price_confirmed` in sync with saved DB value in edit mode.
  useEffect(() => {
    const initialBid = form.value?.initial_bid_price;
    console.log("🚀 ~ IndividualBidderForm ~ initialBid:", initialBid);

    if (
      initialBid === "" ||
      initialBid === null ||
      typeof initialBid === "undefined"
    ) {
      form.setField("initial_bid_price_confirmed", false);
      if (typeof form.removeError === "function")
        form.removeError("initial_bid_price_confirmed");
      else form.pushError({ initial_bid_price_confirmed: [] });
    }

    if (mode === "edit") {
      const saved = bidderData?.meta?.initial_bid_price;
      const savedNum = typeof saved === "string" ? Number(saved) : saved;
      const shouldConfirm =
        savedNum !== undefined &&
        !isNaN(savedNum) &&
        Number(initialBid) === Number(savedNum);
      console.log("🚀 ~ IndividualBidderForm ~ shouldConfirm:", shouldConfirm);
      form.setField("initial_bid_price_confirmed", shouldConfirm);
      if (typeof form.removeError === "function")
        form.removeError("initial_bid_price_confirmed");
      else form.pushError({ initial_bid_price_confirmed: [] });
    } else {
      // creation or no bidderData: leave confirmation false until user checks
      form.setField("initial_bid_price_confirmed", false);
      if (typeof form.removeError === "function")
        form.removeError("initial_bid_price_confirmed");
      else form.pushError({ initial_bid_price_confirmed: [] });
    }
  }, [form.value?.initial_bid_price, bidderData, mode]);

  const handleSubmit = async () => {
    const initialBid = form.getField("initial_bid_price");

    if (
      initialBid !== "" &&
      initialBid !== null &&
      typeof initialBid !== "undefined"
    ) {
      const reservePrice =
        event?.reserve_price ?? event?.property_dump?.reserve_price;
      if (
        reservePrice !== undefined &&
        reservePrice !== null &&
        reservePrice !== ""
      ) {
        if (Number(initialBid) < Number(reservePrice)) {
          form.pushError({
            initial_bid_price: [
              `Initial Bid Price must be greater than or equal to the Reserve Price (₹${Number(
                reservePrice,
              ).toLocaleString("en-IN")}).`,
            ],
          });
          try {
            focusFieldById("initial_bid_price");
          } catch (e) {
            /* ignore */
          }
          return;
        }
      }
    }

    handleBidderDataSubmit(
      form,
      mode,
      id,
      for_event,
      banker,
      bank,
      currentBranch,
      setIsErrorHandlingModalOpen,
      setErrorHandlingModalData,
      randomId,
      kycFiles,
      isOverwrite,
      router,
    );
  };

  if (notFound) return <BankerPageNotFound />;

  return (
    <>
      {
        <div
          className={
            bidderFormWrapperClassName ??
            "lg:pl-9 lg:py-[41px] lg:pr-[70px] space-y-8 lg:space-y-7 lg:mr-[88px] mt-9 lg:mt-6 lg:bg-brand-bgLightGrey"
          }
        >
          {getNonIndividualFormFieldsConfig(user_type ?? "")?.map((field) => (
            <BidderFormFieldsHelper
              key={field.id}
              {...field}
              mode={mode}
              isFetchingPan={isFetchingPan}
            />
          ))}

          <div className="flex flex-col lg:grid lg:grid-cols-2 gap-y-6 mt-3 lg:mt-0  lg:gap-y-5 ">
            {kycDetailsSwitchFields?.map((item: any, index) => (
              <BidderFormFieldsHelper
                key={index}
                id={`asset-${index}`}
                formFieldType="switch_field"
                fieldLabel={item?.title}
                switchFieldValue={
                  form.getField(item?.value) !== ""
                    ? form.getField(item?.value)
                    : false
                }
                setSwitchFieldValue={(checked: boolean) =>
                  form.setField(item?.value, checked)
                }
                disableConditions={mode === "view"}
                mode={mode}
              />
            ))}
          </div>

          <BidderDocumentUploader
            hideConditions={mode === "create" || mode === "edit" ? false : true}
            property_uuid={
              mode === "edit" && typeof id === "string" ? id : randomId
            }
            owner_service_type="kyc-documents"
            dataFiles={kycFiles}
            setDataFiles={setKycFiles}
            allowedMimeTypes={["application/pdf", "image/png"]}
            renderKey={renderKey}
            headingText="Upload KYC documents"
            disableConditions={
              mode === "create" || bidderData?.created_by === "banker"
                ? false
                : true
            }
          />

          <BidderPDFViewer
            hideConditions={mode !== "view"}
            renderKey={renderKey}
            dataFiles={kycFiles}
            setDataFiles={setKycFiles}
            allowedMimeTypes={["application/pdf"]}
            property_uuid={typeof id === "string" ? id : randomId}
            owner_service_type="kyc-documents"
            headingText="View KYC documents"
          />

          <SubmitButtonComponent
            mode={mode}
            handleAcceptAction={handleSubmit}
            acceptButtonDisabled={false}
            buttonLabel={mode === "edit" ? "Update" : "Add Bidder"}
            hideConditions={mode === "view"}
          />

          <AcceptRejectComponent
            mode={mode}
            hideConditions={
              mode !== "view" || bidderData?.created_by !== "user"
            }
            acceptButtonDisabled={
              isLoading ||
              bidderData?.status === "verified" ||
              bidderData?.status === "rejected"
            }
            handleAcceptAction={() => {
              handleAcceptBidder(id);
            }}
            rejectButtonDisabled={
              isLoading ||
              bidderData?.status === "verified" ||
              bidderData?.status === "rejected"
            }
            handleRejectAction={() => {
              setShowRejectionModal(true);
            }}
          />
        </div>
      }

      {showRejectionModal && (
        <CustomRejectionModal
          handleSubmit={(reason: string) => {
            handleRejectBidder(reason, id, setIsLoading);
          }}
          modalType={"rejection"}
          onClose={setShowRejectionModal}
          isLoading={isLoading}
        />
      )}

      {notificationPopup !== "" && (
        <NotificationModal
          isOpen={notificationPopup}
          onClose={setNotificationPopup}
        />
      )}

      {isErrorHandlingModalOpen?.open && (
        <CustomErrorHandlingModal
          errorType={isErrorHandlingModalOpen?.type || "validation"}
          onClose={setIsErrorHandlingModalOpen}
          errorValue={errorHandlingModalData}
          setIsErrorHandlingModalOpen={setIsErrorHandlingModalOpen}
          setErrorHandlingModalData={setErrorHandlingModalData}
          errorHandlingModalData={errorHandlingModalData}
          handleAddBidderAnyway={handleSubmit}
          setIsOverwrite={setIsOverwrite}
        />
      )}
    </>
  );
};

export default NonIndividualBidderForm;
